import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { fetchJson, fetchFormData } from '@/components/inbox/utils'
import type { ChatEditorRef } from '@/components/editor/ChatEditor'
import type { Agent, Message, PendingAttachment } from '@/components/inbox/types'

interface UseMessageComposeParams {
  selectedConversationId: number | null
  currentAgent: Agent
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
}

export function useMessageCompose({
  selectedConversationId,
  currentAgent,
  messages,
  setMessages,
}: UseMessageComposeParams) {
  const [replyingToMessage, setReplyingToMessage] = useState<Message | null>(null)
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [isComposingNew, setIsComposingNew] = useState(false)

  const bodyRef = useRef('')
  const draftPlainTextRef = useRef('')
  const chatEditorRef = useRef<ChatEditorRef>(null)
  const optimisticCounterRef = useRef(0)
  const autoSendAudioRef = useRef(false)

  // Ref wired to the real notifyTyping by useInboxState after WebSocket init
  const notifyTypingRef = useRef<(isTyping: boolean) => void>(() => {})

  // Focus editor when replying
  useEffect(() => {
    if (replyingToMessage) chatEditorRef.current?.focus()
  }, [replyingToMessage])

  // Reset reply on conversation change
  useEffect(() => {
    setReplyingToMessage(null)
  }, [selectedConversationId])

  // Restore persisted draft when switching conversations
  useEffect(() => {
    const editor = chatEditorRef.current?.editor
    if (!editor) return

    const stored = selectedConversationId != null ? readDraft(selectedConversationId) : null
    bodyRef.current = stored?.html ?? ''
    draftPlainTextRef.current = stored?.text ?? ''
    if (stored?.html) {
      editor.commands.setContent(stored.html)
    } else {
      editor.commands.clearContent()
    }
  }, [selectedConversationId])

  // Clean up preview URLs when conversation changes
  useEffect(() => {
    return () => {
      pendingAttachments.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      })
    }
  }, [selectedConversationId])

  // Send "stopped typing" on conversation change or unmount
  useEffect(() => {
    return () => {
      notifyTypingRef.current(false)
    }
  }, [selectedConversationId])

  // Auto-send audio
  useEffect(() => {
    if (autoSendAudioRef.current && pendingAttachments.length > 0) {
      autoSendAudioRef.current = false
      handleSendMessage()
    }
  }, [pendingAttachments])

  function buildMessageUrl(conversationId: number): string {
    return `/conversations/${conversationId}/messages`
  }

  function buildMessageFormData(body: string, repliedToId?: number): FormData {
    const formData = new FormData()
    formData.append('message[body]', body)
    if (repliedToId) formData.append('message[replied_to_message_id]', repliedToId.toString())
    return formData
  }

  function clearComposeUI(): void {
    bodyRef.current = ''
    draftPlainTextRef.current = ''
    setReplyingToMessage(null)
    chatEditorRef.current?.clear()
    if (selectedConversationId != null) clearDraft(selectedConversationId)
  }

  const saveDraft = useCallback((html: string, text: string) => {
    if (selectedConversationId == null) return
    if (text.trim().length === 0) {
      clearDraft(selectedConversationId)
    } else {
      writeDraft(selectedConversationId, html, text)
    }
  }, [selectedConversationId])

  function markOptimisticFailed(tempId: string): void {
    setMessages(prev => prev.map(m => m._tempId === tempId ? { ...m, _sendFailed: true } : m))
  }

  function handleSendMessage(e?: FormEvent) {
    e?.preventDefault()
    if (!selectedConversationId) return

    const hasTextContent = draftPlainTextRef.current.trim().length > 0
    const hasAttachments = pendingAttachments.length > 0
    if (!hasTextContent && !hasAttachments) return

    const messageBody = bodyRef.current
    const currentReplyingTo = replyingToMessage

    // Immediately notify "stopped typing" on send
    notifyTypingRef.current(false)

    const url = buildMessageUrl(selectedConversationId)
    const repliedToId = currentReplyingTo?.id

    const tempId = `optimistic-${++optimisticCounterRef.current}-${Date.now()}`

    // Build optimistic attachments from pending files
    const currentAttachments = [...pendingAttachments]
    const optimisticAttachments = currentAttachments.map((a, i) => ({
      id: -(optimisticCounterRef.current * 100 + i),
      signed_id: '',
      filename: a.file.name,
      content_type: a.file.type,
      byte_size: a.file.size,
      url: a.previewUrl || '',
      is_image: a.file.type.startsWith('image/'),
      is_video: a.file.type.startsWith('video/'),
    }))

    const optimisticMsg: Message = {
      id: -optimisticCounterRef.current,
      body: messageBody,
      channel: 'chat',
      author_type: 'Agent',
      author: { id: currentAgent.id, name: currentAgent.name, email: currentAgent.email, avatar_url: currentAgent.avatar_url },
      created_at: new Date().toISOString(),
      read_at: null, delivered_at: null, deleted_at: null, edited_at: null,
      attachments: optimisticAttachments,
      sender_page_url: null, sender_browser: null, sender_os: null,
      replied_to: currentReplyingTo ? {
        id: currentReplyingTo.id,
        body: currentReplyingTo.body,
        author_type: currentReplyingTo.author_type,
        author_name: currentReplyingTo.author.name,
        attachments_count: currentReplyingTo.attachments?.length || 0,
        deleted: !!currentReplyingTo.deleted_at,
      } : null,
      _optimistic: true, _sendFailed: false, _tempId: tempId,
      _files: hasAttachments ? currentAttachments.map(a => a.file) : undefined,
    }

    setMessages(prev => [...prev, optimisticMsg])
    clearComposeUI()
    if (hasAttachments) setPendingAttachments([])

    const formData = buildMessageFormData(messageBody, repliedToId)
    if (hasAttachments) {
      currentAttachments.forEach((attachment) => formData.append('message[attachments][]', attachment.file))
    }
    fetchFormData(url, formData).then((res) => {
      if (!res.ok) markOptimisticFailed(tempId)
    }).catch(() => markOptimisticFailed(tempId))
  }

  function handleRetryMessage(tempId: string) {
    if (!selectedConversationId) return
    const failedMsg = messages.find(m => m._tempId === tempId)
    if (!failedMsg) return
    setMessages(prev => prev.map(m => m._tempId === tempId ? { ...m, _sendFailed: false } : m))
    const formData = buildMessageFormData(failedMsg.body, failedMsg.replied_to?.id)
    if (failedMsg._files) {
      failedMsg._files.forEach((file) => formData.append('message[attachments][]', file))
    }
    fetchFormData(buildMessageUrl(selectedConversationId), formData).then((res) => {
      if (!res.ok) markOptimisticFailed(tempId)
    }).catch(() => markOptimisticFailed(tempId))
  }

  const deleteMessage = useCallback(async (conversationId: number, messageId: number) => {
    try {
      const response = await fetchJson(`/conversations/${conversationId}/messages/${messageId}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Erro ao excluir mensagem')
    } catch {
      toast.error('Erro ao excluir mensagem')
    }
  }, [])

  const startNewConversation = useCallback(() => {
    setIsComposingNew(true)
  }, [])

  const cancelNewConversation = useCallback(() => {
    setIsComposingNew(false)
  }, [])

  return {
    replyingToMessage,
    setReplyingToMessage,
    pendingAttachments,
    setPendingAttachments,
    isComposingNew,
    bodyRef,
    draftPlainTextRef,
    chatEditorRef,
    autoSendAudioRef,
    notifyTypingRef,
    handleSendMessage,
    handleRetryMessage,
    deleteMessage,
    startNewConversation,
    cancelNewConversation,
    saveDraft,
  }
}

function draftKey(conversationId: number): string {
  return `inbox:draft:${conversationId}`
}

function readDraft(conversationId: number): { html: string; text: string } | null {
  try {
    const raw = localStorage.getItem(draftKey(conversationId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.html !== 'string' || typeof parsed?.text !== 'string') return null
    return parsed
  } catch {
    return null
  }
}

function writeDraft(conversationId: number, html: string, text: string): void {
  try {
    localStorage.setItem(draftKey(conversationId), JSON.stringify({ html, text }))
  } catch {
    // localStorage may throw on quota or in privacy mode
  }
}

function clearDraft(conversationId: number): void {
  localStorage.removeItem(draftKey(conversationId))
}
